import { useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { SectionCard } from "shared/app/SectionCard";
import { Select, Button, ButtonVariant, ButtonSize } from "shared/components";
import type { BaseOption } from "shared/components";
import { useInboxToken } from "../../inbox/api/useInboxToken";
import {
  usePhoneNumbers,
  usePropertyPhoneDefault,
  useSetPropertyPhoneDefault
} from "../../settings-communication/api/hooks";
import { formatPhone } from "shared/utils/format-phone";

interface CommunicationDefaultsSectionProps {
  propertyId: number;
}

export const CommunicationDefaultsSection = ({
  propertyId
}: CommunicationDefaultsSectionProps) => {
  const { accessToken } = useInboxToken();
  const { data } = usePhoneNumbers(!!accessToken);
  const { data: currentDefault } = usePropertyPhoneDefault(
    propertyId,
    !!accessToken
  );
  const { mutate: setPropertyDefault } = useSetPropertyPhoneDefault();

  const activeNumbers = useMemo(
    () => data?.phone_numbers.filter((pn) => pn.status === "active") ?? [],
    [data]
  );

  const options: BaseOption[] = useMemo(
    () =>
      activeNumbers.map((pn) => ({
        value: pn.id,
        label: formatPhone(pn.phone_number ?? "")
      })),
    [activeNumbers]
  );

  const partnerDefaultId = activeNumbers.find((pn) => pn.is_default)?.id;

  const selectedValue =
    currentDefault?.phone_number_id ||
    partnerDefaultId ||
    activeNumbers[0]?.id ||
    "";

  const handleChange = (e: { target: { value: string } }) => {
    setPropertyDefault(
      { propertyId, phoneNumberId: e.target.value },
      {
        onSuccess: () => {
          toast.success("Property phone number default updated");
        },
        onError: (error) => {
          const message = axios.isAxiosError<{ message?: string }>(error)
            ? error.response?.data.message
            : undefined;
          toast.error(message || "Failed to update default");
        }
      }
    );
  };

  return (
    <SectionCard
      title="Communication defaults"
      subtitle="Applicants will receive communications from this entity"
    >
      <div>
        <label className="block text-sm font-medium text-content-600 mb-1">
          Phone number
        </label>
        {activeNumbers.length > 0 ? (
          <Select
            id={`property-${propertyId}-phone-default`}
            hideOptional
            value={selectedValue}
            onChange={handleChange}
            options={options}
            legacyErrorMargins={false}
            className="max-w-[280px]"
          />
        ) : (
          <Link to="/settings/communication">
            <Button variant={ButtonVariant.BASE} size={ButtonSize.SM}>
              Set up phone numbers
            </Button>
          </Link>
        )}
      </div>
    </SectionCard>
  );
};
